import { Schema } from "effect";
import { QueryOptions } from "./query-options";

/**
 * Default pagination values
 */
export const DEFAULT_LIMIT = 100;
export const MAX_LIMIT = 500;

/**
 * Schema for cursor-based pagination query parameters.
 * The cursor is an opaque string representing the position in the result set.
 * Uses NumberFromString since URL params are always strings.
 */
export const CursorPaginationParams = <A, I, R>(
  cursorSchema: Schema.Schema<A, I, R>,
) =>
  Schema.Struct({
    limit: Schema.optionalWith(
      Schema.NumberFromString.pipe(
        Schema.int(),
        Schema.positive(),
        Schema.lessThanOrEqualTo(MAX_LIMIT),
      ),
      { default: () => DEFAULT_LIMIT },
    ),
    cursor: Schema.optional(
      Schema.compose(
        Schema.StringFromUriComponent,
        Schema.parseJson(cursorSchema),
      ),
    ),
  });

/**
 * Schema for cursor-based pagination with TanStack DB query options.
 * Includes filters and sorts from the TanStack DB parseLoadSubsetOptions format.
 */
export const CursorPaginationWithQueryParams = <A, I, R>(
  cursorSchema: Schema.Schema<A, I, R>,
) =>
  Schema.Struct({
    limit: Schema.optionalWith(
      Schema.NumberFromString.pipe(
        Schema.int(),
        Schema.positive(),
        Schema.lessThanOrEqualTo(MAX_LIMIT),
      ),
      { default: () => DEFAULT_LIMIT },
    ),
    cursor: Schema.optional(
      Schema.compose(
        Schema.StringFromUriComponent,
        Schema.parseJson(cursorSchema),
      ),
    ),
    queryOptions: Schema.optional(
      Schema.compose(
        Schema.StringFromUriComponent,
        Schema.parseJson(QueryOptions),
      ),
    ),
  });

/**
 * Schema for cursor-based pagination with conversation filter group.
 * The filterGroup is a URL-encoded string in the format used by encodeAdvancedFilterGroup.
 */
export const CursorPaginationWithConversationQueryParams = <A, I, R>(
  cursorSchema: Schema.Schema<A, I, R>,
) =>
  Schema.Struct({
    limit: Schema.optionalWith(
      Schema.NumberFromString.pipe(
        Schema.int(),
        Schema.positive(),
        Schema.lessThanOrEqualTo(MAX_LIMIT),
      ),
      { default: () => DEFAULT_LIMIT },
    ),
    cursor: Schema.optional(
      Schema.compose(
        Schema.StringFromUriComponent,
        Schema.parseJson(cursorSchema),
      ),
    ),
    filterGroup: Schema.optional(Schema.String),
  });

/**
 * Creates a cursor-paginated response schema for a given item schema.
 * Returns items along with a cursor for fetching the next page.
 */
export const CursorPaginatedResponse = <A, I, R, A2, I2, R2>(
  itemSchema: Schema.Schema<A, I, R>,
  cursorSchema: Schema.Schema<A2, I2, R2>,
) =>
  Schema.Struct({
    items: Schema.Array(itemSchema),
    nextCursor: Schema.NullOr(cursorSchema),
  });
